import { AxiosRequestConfig, AxiosResponse } from "axios";
import { parseApiData } from "go-core/api/parseApiData";
import { FILTER_VALUE_SEPARATOR } from "go-list/core/components/Filter/services";
import { RemoveOrganizationSecurityApi } from "../../../lib/go-security/services/Api/typesSecurity";
import { OrganizationApplicationAccesses } from "../../../pages/App/pages/Organization/pages/ServiceAccess/components/SupportAccessPreview";
import { restApi } from "../../Security/api";
import OrganizationIdService from "../../organizationIdService";
import { SupportAccessOrganizationApplicationLogApi } from "../../support/types";
import {
	PauseSubscriptionApi,
	ResumeSubscriptionApi,
	SubscriptionInvoiceApi,
	SubscriptionVisibilityApi,
	SubscriptionsApi,
} from "../Subscription/subscriptionTypes";
import { AcceptOrganizationTermsSecurityApi, LoanApi, OrganizationApplicationApi, OrganizationMeApi } from "../types";
import {
	InvitationApi,
	OrganizationInvitationLinkApi,
	OrganizationUserApi,
	PaymentSourceApi,
	SupportAccessFormProps,
	UserWithInviteApi,
	VerifyTermApi,
} from "./organizationTypes";

class organizationApi {
	static getOrganizationId = () => {
		return OrganizationIdService.getWithCheck();
	};

	static async getOrganizationMe(id: number | string): Promise<OrganizationMeApi> {
		return parseApiData(restApi.get(`/${id}/me`));
	}

	static async getUsersWithInvitations(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<UserWithInviteApi[]> {
		return parseApiData(
			restApi.get(`/${this.getOrganizationId()}/users/with-invitations`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getOrganizationUser(
		id: number,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<OrganizationUserApi> {
		return parseApiData(
			restApi.get(`/${this.getOrganizationId()}/users/${id}`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getOrganizationApplications(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<OrganizationApplicationApi[]> {
		return parseApiData(
			restApi.get(`/${this.getOrganizationId()}/applications`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async acceptOrganizationTerms(data: AcceptOrganizationTermsSecurityApi): Promise<AxiosResponse> {
		return restApi.put(`/${this.getOrganizationId()}/accept_terms`, data);
	}

	static async removeUserFromOrganization(userId: number): Promise<AxiosResponse> {
		return restApi.delete(`/${this.getOrganizationId()}/users/${userId}`);
	}

	static async removeUserInvitation(inviteId: number): Promise<AxiosResponse> {
		return restApi.delete(`/${this.getOrganizationId()}/invitations/${inviteId}`);
	}

	static async resendInvitation(inviteId: number): Promise<AxiosResponse> {
		return restApi.post(`/${this.getOrganizationId()}/invitations/${inviteId}/resend`);
	}

	static async acceptInvitation(inviteId: number): Promise<UserWithInviteApi> {
		return parseApiData(restApi.post(`/${this.getOrganizationId()}/invitations/${inviteId}/accept`));
	}

	static async getSubscriptionInvoices(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<SubscriptionInvoiceApi[]> {
		const parsedData = await parseApiData(
			restApi.get(`/${this.getOrganizationId()}/subscriptions/invoices`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
		return parsedData.invoices;
	}

	static async generateInvoice(invoiceId: string): Promise<AxiosResponse> {
		return parseApiData(
			restApi.post(`/${this.getOrganizationId()}/subscriptions/invoices/proforma/${invoiceId}/generate_invoice`)
		);
	}

	static async getSubscriptionInvoicePaymentStatus(id?: string): Promise<string> {
		return parseApiData(
			restApi.get(`/${this.getOrganizationId()}/subscriptions/invoices/proforma/${id}/payment_status`)
		);
	}

	static async getSubscriptionInvoicesPaymentStatus(): Promise<AxiosResponse> {
		return restApi.get(`/${this.getOrganizationId()}/subscriptions/invoices/proforma/payment_status`);
	}

	static async getInvitation(
		id: number,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<InvitationApi> {
		return parseApiData(
			restApi.get(`/${this.getOrganizationId()}/invitations/${id}`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getOrganizationSubscriptions(
		organizationId: number,
		params?: Record<string, any>
	): Promise<SubscriptionsApi> {
		return parseApiData(restApi.get(`/${organizationId}/subscriptions`, { params }));
	}

	static async getSubscriptions(params?: Record<string, any>): Promise<AxiosResponse> {
		return restApi.get(`/${this.getOrganizationId()}/subscriptions`, { params });
	}

	static async getAvailableSubscriptionContents(): Promise<AxiosResponse> {
		return restApi.get(`/${this.getOrganizationId()}/subscriptions/available_contents`);
	}

	static async getSubscriptionsVisibility(): Promise<SubscriptionVisibilityApi> {
		return parseApiData(restApi.get(`/${this.getOrganizationId()}/subscriptions/visibility`));
	}

	static async pauseSubscription(data: PauseSubscriptionApi): Promise<AxiosResponse> {
		return restApi.post(`/${this.getOrganizationId()}/subscriptions/pause`, data);
	}

	static async cancelSubscription(data: any): Promise<AxiosResponse> {
		return restApi.post(`/${this.getOrganizationId()}/subscriptions/cancel`, data);
	}

	static async resumeSubscription(data: ResumeSubscriptionApi): Promise<AxiosResponse> {
		return restApi.post(`/${this.getOrganizationId()}/subscriptions/resume`, data);
	}

	static async estimateSubscription(data: any, options?: any): Promise<AxiosResponse> {
		return restApi.post(`/${this.getOrganizationId()}/subscriptions/estimate`, data, {
			cancelToken: options?.cancelToken,
		});
	}

	static async updateSubscription(data: any): Promise<AxiosResponse> {
		return restApi.post(`/${this.getOrganizationId()}/subscriptions`, data);
	}

	static async getSubscriptionProformaPdf(proformaId: string): Promise<AxiosResponse> {
		return restApi.get(`/${this.getOrganizationId()}/subscriptions/invoices/proforma/${proformaId}/pdf`, {
			responseType: "blob",
		});
	}

	static async getSubscriptionInvoicePdf(proformaId: string): Promise<AxiosResponse> {
		return restApi.get(`/${this.getOrganizationId()}/subscriptions/invoices/proforma/${proformaId}/invoice/pdf`, {
			responseType: "blob",
		});
	}

	static async syncSubscription(): Promise<AxiosResponse> {
		return restApi.post(`/${this.getOrganizationId()}/subscriptions/sync`);
	}

	static async paySubscriptionProformaOnline(proformaId: string): Promise<AxiosResponse> {
		return restApi.post(
			`/${this.getOrganizationId()}/subscriptions/invoices/proforma/${proformaId}/start_transfer_payment`
		);
	}

	static async paySubscriptionProformaByCard(proformaId: string): Promise<AxiosResponse> {
		return restApi.post(
			`/${this.getOrganizationId()}/subscriptions/invoices/proforma/${proformaId}/start_card_payment`
		);
	}

	static billingCardPreTransaction(redirectUrl: string): Promise<AxiosResponse> {
		return restApi.post(`/${this.getOrganizationId()}/billing/card/pre_transaction?redirect_url=${redirectUrl}`);
	}

	static async updateInvitation(id: number, data: InvitationApi): Promise<AxiosResponse> {
		return restApi.put(`/${this.getOrganizationId()}/invitations/${id}?include=applications,permissions`, data);
	}

	static async updateOrganizationUser(id: number, data: OrganizationUserApi): Promise<OrganizationUserApi> {
		return parseApiData(restApi.put(`/${this.getOrganizationId()}/users/${id}`, data));
	}

	static async inviteUser(invitation: InvitationApi): Promise<AxiosResponse> {
		return restApi.post(`/${this.getOrganizationId()}/invitations`, invitation);
	}

	static async removeScheduledSubscriptionChanges(data: any): Promise<AxiosResponse> {
		return restApi.post(`/${this.getOrganizationId()}/subscriptions/remove_scheduled_changes`, data);
	}

	static async migrateSubscription(): Promise<AxiosResponse> {
		return restApi.post(`/${this.getOrganizationId()}/subscriptions/migrate`);
	}

	static async postBillingPreAuthorizationCard(redirect_url?: string): Promise<AxiosResponse> {
		return restApi.post(
			`/${this.getOrganizationId()}/billing/card/preauthorize?redirect_url=${redirect_url || ""}`,
			{}
		);
	}

	static async deleteBillingCard(): Promise<AxiosResponse> {
		return restApi.delete(`/${this.getOrganizationId()}/billing/card`, {});
	}

	static async activateBillingCard(): Promise<AxiosResponse> {
		return restApi.put(`/${this.getOrganizationId()}/billing/card/validate`);
	}

	static async getBillingCard(): Promise<PaymentSourceApi> {
		return restApi.get(`/${this.getOrganizationId()}/billing/card`, {});
	}

	static async getLoans(params?: Record<string, any>, options?: Record<string, any>): Promise<LoanApi[]> {
		return parseApiData(
			restApi.get(`/${this.getOrganizationId()}/loans`, { params, cancelToken: options?.cancelToken })
		);
	}

	static async getLoan(id: number, params?: Record<string, any>): Promise<LoanApi> {
		return parseApiData(restApi.get(`/${this.getOrganizationId()}/loans/${id}`, { params }));
	}

	static async getEstimatedLoan(id: number, params?: Record<string, any>): Promise<SubscriptionsApi> {
		return parseApiData(restApi.get(`/${this.getOrganizationId()}/loans/${id}/estimate`, { params }));
	}

	static async acceptLoan(id: number): Promise<LoanApi> {
		return parseApiData(restApi.put(`/${this.getOrganizationId()}/loans/${id}/accept`));
	}

	static async getSupportAccess(): Promise<{ applications: OrganizationApplicationAccesses[] }> {
		return parseApiData(restApi.get(`/${this.getOrganizationId()}/support_access`));
	}

	static async updateSupportAccess(data: SupportAccessFormProps): Promise<AxiosResponse> {
		return restApi.put(`/${this.getOrganizationId()}/support_access`, data);
	}

	static async removeOrganization(data: RemoveOrganizationSecurityApi): Promise<AxiosResponse> {
		const config = { data } as AxiosRequestConfig;
		return restApi.delete(`/organizations/${this.getOrganizationId()}`, config);
	}

	static async generateRemoveOrganizationToken(): Promise<AxiosResponse> {
		return restApi.post(`/organizations/${this.getOrganizationId()}/delete_organization_confirmation_token`);
	}

	static async getOrganizationSupportAccessLogs(
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<SupportAccessOrganizationApplicationLogApi[]> {
		return parseApiData(
			restApi.get(`/${this.getOrganizationId()}/support_access/logs`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async generateLoanTerminationFile(loanId: number): Promise<AxiosResponse> {
		return restApi.get(`/${this.getOrganizationId()}/loans/${loanId}/terminate/pdf`, {
			responseType: "blob",
		});
	}

	static async generateInvitationLink(id: number): Promise<OrganizationInvitationLinkApi> {
		return parseApiData(restApi.get(`/${this.getOrganizationId()}/invitations/${id}/invitation_link/`));
	}

	static async verifyTerms(termNames: string[]): Promise<{ required_terms: VerifyTermApi[] }> {
		const termsName = termNames.join(FILTER_VALUE_SEPARATOR);
		const filter = btoa(unescape(encodeURIComponent(`terms_name=${termsName}`)));
		return parseApiData(
			restApi.get(`/${this.getOrganizationId()}/terms/verify_signed_by_organization?f=${filter}`)
		);
	}

	static async acceptTerms(termsName: string): Promise<{}> {
		return parseApiData(
			restApi.post(`/${this.getOrganizationId()}/terms/accept`, {
				terms_name: termsName,
			})
		);
	}
}

export { organizationApi };
